import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Button,
  LinearProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  OpenInNew as OpenInNewIcon,
  YouTube as YouTubeIcon,
  Facebook as MetaIcon,
  CloudDownload as BatchDownloadIcon,
  FileDownload as ExportIcon,
  SportsEsports as AppLovinIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { AdRecord, getMeantToPlatforms } from '../types';
import { useAuth } from '../contexts/AuthContext';
import CreateMetaAdDialog from '../components/CreateMetaAdDialog';
import ApplovinUploadDialog from '../components/ApplovinUploadDialog';

interface ServerFilters {
  production_id: number | null;
  creative_number: number | null;
  created_by: string | null;
  upload_status: string | null;
  meant_to: string[];
  media_type: string | null;
  date_from: string | null;
  date_to: string | null;
}

const EMPTY_FILTERS: ServerFilters = {
  production_id: null,
  creative_number: null,
  created_by: null,
  upload_status: null,
  meant_to: [],
  media_type: null,
  date_from: null,
  date_to: null,
};

const PAGE_SIZE = 50;

const META_UPLOAD_ENABLED = (process.env.REACT_APP_META_UPLOAD_ENABLED || 'true').toLowerCase() === 'true';
const getErrorMessage = (error: unknown): string => error instanceof Error ? error.message : 'Unknown error';
const isUploadedToAnyMeta = (ad: AdRecord): boolean => {
  if (ad.meta_ad_id) return true;
  const ids = ad.meta_ad_ids;
  return ids ? Object.keys(ids).length > 0 : false;
};

const getMetadataString = (metadata: AdRecord['metadata'] | undefined, key: string): string => {
  const value = metadata?.[key];
  return typeof value === 'string' ? value.toLowerCase() : '';
};

const getFileMetadataContentType = (metadata: AdRecord['metadata'] | undefined): string => {
  const fileMetadata = metadata?.file_metadata;
  if (fileMetadata && typeof fileMetadata === 'object' && 'content_type' in fileMetadata) {
    const contentType = fileMetadata.content_type;
    return typeof contentType === 'string' ? contentType.toLowerCase() : '';
  }
  return '';
};

const getAdUrlPath = (url: string): string => {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
};

const isHtmlAd = (ad: AdRecord): boolean => {
  const contentType = getFileMetadataContentType(ad.metadata);
  const mediaType = getMetadataString(ad.metadata, 'media_type');
  const path = getAdUrlPath(ad.video_url || '');
  return mediaType === 'html'
    || contentType.startsWith('text/html')
    || /\.(html|htm)$/i.test(path);
};

const isImageAd = (ad: AdRecord): boolean => {
  try {
    const contentType = getFileMetadataContentType(ad.metadata);
    const mediaType = getMetadataString(ad.metadata, 'media_type');
    const path = getAdUrlPath(ad.video_url || '');
    if (isHtmlAd(ad)) return false;
    if (ad.duration === 0) return true;
    return (mediaType === 'image')
      || contentType.startsWith('image/')
      || /\.(jpg|jpeg|png|webp)$/i.test(path);
  } catch {
    const path = getAdUrlPath(ad.video_url || '');
    return !/\.(html|htm)$/i.test(path) && (/\.(jpg|jpeg|png|webp)$/i.test(path) || ad.duration === 0);
  }
};

const AdsTablePage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // Server-side filter state
  const [filters, setFilters] = useState<ServerFilters>(EMPTY_FILTERS);
  const [includeDisabled, setIncludeDisabled] = useState(false);

  // Cursor-based pagination
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const [thumbnails, setThumbnails] = useState<Record<string, string | null>>({});
  
  // Batch selection state
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  
  // Batch upload state
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchUploadType, setBatchUploadType] = useState<'youtube' | 'meta' | 'applovin' | 'download' | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ completed: number; total: number; current?: string }>({ completed: 0, total: 0 });
  const [batchResults, setBatchResults] = useState<Array<{ ad_id: string; success: boolean; error?: string }>>([]);
  const [isRefetchingAfterUpload, setIsRefetchingAfterUpload] = useState(false);
  
  // UI state
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'info' });
  
  // Create Meta Ad dialog state
  const [showCreateMetaAdDialog, setShowCreateMetaAdDialog] = useState(false);

  // AppLovin upload dialog state
  const [showApplovinDialog, setShowApplovinDialog] = useState(false);
  const pendingApplovinTemplate = useRef<string | null>(null);
  const pendingApplovinCampaign = useRef<string | null>(null);
  
  // Meta account picker state
  const [metaAccounts, setMetaAccounts] = useState<Array<{ key: string; label: string; ad_account_id: string }>>([]);
  const [showMetaAccountPicker, setShowMetaAccountPicker] = useState(false);
  const [selectedMetaAccount, setSelectedMetaAccount] = useState<string>('default');
  const [pendingMetaUploadAds, setPendingMetaUploadAds] = useState<string[]>([]);

  // Build query params for the paginated ads fetch
  const adsQueryParams = {
    page_size: PAGE_SIZE,
    cursor,
    ...filters,
    meant_to: filters.meant_to.length > 0 ? filters.meant_to : null,
    include_disabled: includeDisabled,
  };

  // Fetch paginated ads
  const {
    data: adsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ads-paginated', adsQueryParams],
    queryFn: () => apiService.getAdsPage(adsQueryParams),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  const ads = adsResponse?.ads ?? [];
  const nextCursor = adsResponse?.next_cursor ?? null;
  const totalCount = adsResponse?.total_count ?? 0;

  // Build query params for filter options (exclude cursor -- options don't change per page)
  const filterOptionsParams = {
    ...filters,
    meant_to: filters.meant_to.length > 0 ? filters.meant_to : null,
    include_disabled: includeDisabled,
  };

  // Fetch cascading filter options
  const { data: filterOptions } = useQuery({
    queryKey: ['ads-filter-options', filterOptionsParams],
    queryFn: () => apiService.getAdsFilterOptions(filterOptionsParams),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Reset cursor when filters change
  const updateFilter = useCallback(<K extends keyof ServerFilters>(key: K, value: ServerFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCursor(null);
    setCursorHistory([]);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setCursor(null);
    setCursorHistory([]);
  }, []);

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      setCursorHistory(prev => [...prev, cursor ?? '']);
      setCursor(nextCursor);
    }
  }, [nextCursor, cursor]);

  const handlePrevPage = useCallback(() => {
    setCursorHistory(prev => {
      const newHistory = [...prev];
      const prevCursor = newHistory.pop();
      setCursor(prevCursor === '' ? null : prevCursor ?? null);
      return newHistory;
    });
  }, []);

  const currentPageNumber = cursorHistory.length + 1;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Fetch Meta accounts on mount
  useEffect(() => {
    if (META_UPLOAD_ENABLED) {
      apiService.getMetaAccounts()
        .then(data => {
          setMetaAccounts(data.accounts);
          if (data.accounts.length > 0) {
            setSelectedMetaAccount(data.accounts[0].key);
          }
        })
        .catch(err => console.warn('Failed to load Meta accounts:', err));
    }
  }, []);

  // Batch selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAds(prev => {
        const next = new Set(prev);
        paginatedAds.filter(ad => !isHtmlAd(ad)).forEach(ad => next.add(ad.ad_id));
        return next;
      });
      setIsAllSelected(true);
    } else {
      setSelectedAds(prev => {
        const next = new Set(prev);
        paginatedAds.forEach(ad => next.delete(ad.ad_id));
        return next;
      });
      setIsAllSelected(false);
    }
  };

  const handleSelectAd = (adId: string, checked: boolean) => {
    const newSelected = new Set(selectedAds);
    if (checked) {
      newSelected.add(adId);
    } else {
      newSelected.delete(adId);
      setIsAllSelected(false);
    }
    setSelectedAds(newSelected);
  };


  // Batch action handlers
  const handleBatchUpload = async (type: 'youtube' | 'meta' | 'applovin', accountKey?: string) => {
    const selectedAdsList = Array.from(selectedAds);
    if (selectedAdsList.length === 0) {
      setSnackbar({ open: true, message: 'Please select ads to upload', severity: 'error' });
      return;
    }

    const selectedHtmlAdIds = new Set(
      paginatedAds
        .filter(ad => selectedAds.has(ad.ad_id) && isHtmlAd(ad))
        .map(ad => ad.ad_id)
    );
    const uploadableAdsList = selectedAdsList.filter(adId => !selectedHtmlAdIds.has(adId));
    const skippedHtmlResults = Array.from(selectedHtmlAdIds).map(adId => ({
      ad_id: adId,
      success: false,
      error: 'HTML creatives cannot be uploaded to platforms from this workflow'
    }));

    if (uploadableAdsList.length === 0) {
      setBatchResults(skippedHtmlResults);
      setShowBatchDialog(skippedHtmlResults.length > 0);
      setSnackbar({ open: true, message: 'Selected HTML creatives cannot be uploaded to platforms from this workflow', severity: 'error' });
      return;
    }

    // For Meta uploads with multiple accounts, show the account picker first
    if (type === 'meta' && metaAccounts.length > 1 && !accountKey) {
      setPendingMetaUploadAds(uploadableAdsList);
      setShowMetaAccountPicker(true);
      return;
    }

    const effectiveAccountKey = accountKey || selectedMetaAccount || 'default';

    setBatchUploadType(type);
    setIsBatchUploading(true);
    setBatchProgress({ completed: 0, total: uploadableAdsList.length });
    setBatchResults([]);

    try {
      let batchResult;
      if (type === 'youtube') {
        batchResult = await apiService.batchUploadToYouTube(uploadableAdsList);
      } else if (type === 'applovin') {
        const templateId = pendingApplovinTemplate.current;
        const campId = pendingApplovinCampaign.current;
        if (!templateId || !campId) {
          throw new Error('Missing AppLovin creative set ID or campaign ID');
        }
        batchResult = await apiService.batchUploadToApplovin(uploadableAdsList, templateId, campId);
        pendingApplovinTemplate.current = null;
        pendingApplovinCampaign.current = null;
      } else {
        batchResult = await apiService.batchUploadToMeta(uploadableAdsList, effectiveAccountKey);
      }

      // Convert batch result to our expected format
      const results: Array<{ ad_id: string; success: boolean; error?: string }> = [
        ...batchResult.successful_jobs.map(job => ({ ad_id: job.ad_id, success: true })),
        ...batchResult.failed_jobs.map(job => ({ ad_id: job.ad_id, success: false, error: job.error })),
        ...skippedHtmlResults
      ];

      setBatchProgress({ completed: uploadableAdsList.length, total: uploadableAdsList.length });
      setBatchResults(results);
      setShowBatchDialog(true);

      setSnackbar({
        open: true,
        message: skippedHtmlResults.length > 0 ? `${batchResult.message}; skipped ${skippedHtmlResults.length} HTML creative(s)` : batchResult.message,
        severity: results.every(result => result.success) ? 'success' : 'error'
      });

      // Poll for job completion before refetching data
      if (batchResult.successful_jobs.length > 0) {
        setIsRefetchingAfterUpload(true);
        const jobIds = batchResult.successful_jobs.map(job => job.job_id);
        
        // Poll every 3 seconds, max 60 seconds (20 attempts)
        const maxAttempts = 20;
        const pollInterval = 3000;
        let attempts = 0;
        
        const pollJobs = async (): Promise<boolean> => {
          try {
            const jobStatuses = await Promise.all(
              jobIds.map(jobId => apiService.getJobStatus(jobId))
            );
            
            const allComplete = jobStatuses.every(
              job => job.status === 'completed' || job.status === 'failed'
            );
            
            return allComplete;
          } catch (err) {
            console.error('Error polling job status:', err);
            return false;
          }
        };
        
        // Wait for jobs to complete
        while (attempts < maxAttempts) {
          const allDone = await pollJobs();
          if (allDone) {
            break;
          }
          attempts++;
          await new Promise<void>((resolve) => setTimeout(resolve, pollInterval));
        }

        // Check completed jobs for warnings
        try {
          const finalStatuses = await Promise.all(
            jobIds.map(jobId => apiService.getJobStatus(jobId))
          );
          const warnings = finalStatuses
            .filter(job => job.platform_response?.warning)
            .map(job => job.platform_response.warning);
          if (warnings.length > 0) {
            setSnackbar({
              open: true,
              message: `Upload completed with warning: ${warnings[0]}`,
              severity: 'warning',
            });
          }
        } catch {
          // Ignore errors checking for warnings
        }
        
        // Now refetch to get updated data
        await refetch();
        setIsRefetchingAfterUpload(false);
      }

    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Failed to initiate batch ${type} upload: ${getErrorMessage(error)}`,
        severity: 'error'
      });
    } finally {
      setIsBatchUploading(false);
      setIsRefetchingAfterUpload(false);
      // Keep selection active so user can proceed to "Create Meta Ad"
      // setSelectedAds(new Set()); ❌ Removed - selection persists
    }
  };

  const handleMetaAccountPickerConfirm = () => {
    setShowMetaAccountPicker(false);
    handleBatchUpload('meta', selectedMetaAccount);
  };

  const handleApplovinDialogConfirm = (templateCreativeSetId: string, campaignId: string) => {
    pendingApplovinTemplate.current = templateCreativeSetId;
    pendingApplovinCampaign.current = campaignId;
    setShowApplovinDialog(false);
    handleBatchUpload('applovin');
  };

  const handleBatchDownload = async () => {
    const selectedAdsList = Array.from(selectedAds);

    if (selectedAdsList.length === 0) {
      setSnackbar({ open: true, message: 'Please select ads to download', severity: 'error' });
      return;
    }

    setBatchUploadType('download');
    setIsBatchUploading(true);
    setBatchProgress({ completed: 0, total: selectedAdsList.length });
    
    try {
      setSnackbar({ 
        open: true, 
        message: `Creating zip file with ${selectedAdsList.length} videos...`, 
        severity: 'info' 
      });
      
      const result = await apiService.batchDownloadVideos(selectedAdsList);
      
      setBatchProgress({ completed: selectedAdsList.length, total: selectedAdsList.length });
      
      // Automatically trigger download
      const link = document.createElement('a');
      link.href = result.download_url;
      link.download = `ads_batch_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show results
      const zipSizeMB = (result.zip_size / 1024 / 1024).toFixed(1);
      setSnackbar({ 
        open: true, 
        message: `Download started! ZIP file (${zipSizeMB}MB) contains ${result.successful_count} videos${result.failed_count > 0 ? `, ${result.failed_count} failed` : ''}. Link expires in ${result.expires_in}.`, 
        severity: result.failed_count === 0 ? 'success' : 'info'
      });
      
      // Store failed results if any
      if (result.failed_ads.length > 0) {
        setBatchResults(result.failed_ads.map(failed => ({ 
          ad_id: failed.ad_id, 
          success: false, 
          error: failed.error 
        })));
        setShowBatchDialog(true);
      }
      
    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Failed to create batch download: ${getErrorMessage(error)}`,
        severity: 'error'
      });
    } finally {
      setIsBatchUploading(false);
      setSelectedAds(new Set());
    }
  };

  // The current page of ads is directly from the server response
  const paginatedAds = ads;

  // Update isAllSelected when ads or selection changes
  React.useEffect(() => {
    if (paginatedAds.length > 0) {
      const currentPageAdIds = paginatedAds.filter(ad => !isHtmlAd(ad)).map(ad => ad.ad_id);
      const allCurrentPageSelected = currentPageAdIds.length > 0 && currentPageAdIds.every(id => selectedAds.has(id));
      setIsAllSelected(allCurrentPageSelected && selectedAds.size > 0);
    }
  }, [paginatedAds, selectedAds]);

  // Export to CSV function (exports current page)
  const exportToCSV = () => {
    try {
      const adsToExport = paginatedAds;
      
      if (adsToExport.length === 0) {
        setSnackbar({
          open: true,
          message: 'No ads to export',
          severity: 'info'
        });
        return;
      }

      // Define CSV headers
      const headers = [
        'Ad ID',
        'Production ID',
        'Creative Number',
        'Name',
        'Language',
        'Ratio',
        'Duration (s)',
        'Created By',
        'Updated At',
        'Cast',
        'Hero Age',
        'Hero Gender',
        'Licensed Songs',
        'Meant To',
        'YouTube ID',
        'Meta ID',
        'Status',
        'Video URL'
      ];

      // Generate CSV rows
      const rows = adsToExport.map(ad => {
        const licensedSongs = ad.tags && Array.isArray(ad.tags.licensedSong) 
          ? ad.tags.licensedSong.join('; ')
          : ad.tags?.licensedSong || '';
        
        const meantTo = ad.tags?.meantTo?.join('; ') || '';
        
        return [
          ad.ad_id,
          ad.production_id,
          ad.creative_number,
          ad.name_en,
          ad.language,
          ad.ratio,
          ad.duration,
          ad.created_by,
          new Date(ad.updated_at).toLocaleString(),
          ad.tags?.cast || 'unknown',
          ad.tags?.heroAge || 'unknown',
          ad.tags?.heroGender || 'unknown',
          licensedSongs,
          meantTo,
          ad.youtube_video_id || '',
          ad.meta_ad_id || '',
          ad.status,
          ad.video_url
        ];
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => 
          row.map(cell => {
            // Escape cells that contain commas, quotes, or newlines
            const cellStr = String(cell || '');
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        )
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `ads_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Exported ${adsToExport.length} ads to CSV`,
        severity: 'success'
      });
    } catch (error: unknown) {
      console.error('Failed to export CSV:', error);
      setSnackbar({
        open: true,
        message: `Failed to export CSV: ${getErrorMessage(error)}`,
        severity: 'error'
      });
    }
  };

  // Generate a thumbnail (first frame) for a video URL
  const generateVideoThumbnailFromUrl = (url: string): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
    try {
      const video = document.createElement('video');
      let resolved = false;
      const cleanup = () => {
        try {
          video.pause();
          video.src = '';
          video.removeAttribute('src');
          video.load();
        } catch {}
      };

      const finishWithCanvas = () => {
        try {
          const width = video.videoWidth || 0;
          const height = video.videoHeight || 0;
          if (!width || !height) { cleanup(); resolve(null); return; }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { cleanup(); resolve(null); return; }
          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg');
          cleanup();
          resolve(dataUrl);
        } catch {
          cleanup();
          resolve(null);
        }
      };

      const onSeeked = () => {
        if (resolved) return;
        resolved = true;
        finishWithCanvas();
      };

      video.addEventListener('loadedmetadata', () => {
        try {
          // Seek to the first frame
          video.currentTime = 0.001;
        } catch {
          finishWithCanvas();
        }
      });

      video.addEventListener('seeked', onSeeked, { once: true });
      video.addEventListener('error', () => { if (!resolved) { resolved = true; cleanup(); resolve(null); } });

      // Try to avoid tainting canvas when CORS allows
      try { video.crossOrigin = 'anonymous'; } catch {}
      video.preload = 'auto';
      video.muted = true;
      // Some browsers respect #t fragment to preload near 0s
      video.src = url.includes('#') ? url : `${url}#t=0.001`;
      // iOS requires load() sometimes
      try { video.load(); } catch {}

      // Fallback timeout
      setTimeout(() => { if (!resolved) { resolved = true; cleanup(); resolve(null); } }, 5000);
    } catch {
      resolve(null);
    }
    });
  };

  // Build thumbnails for current page rows if missing
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const ad of paginatedAds) {
        if (isHtmlAd(ad) || isImageAd(ad)) continue; // static creatives render directly or use placeholder
        if (thumbnails.hasOwnProperty(ad.ad_id)) continue;
        const thumb = await generateVideoThumbnailFromUrl(ad.video_url);
        if (!cancelled) setThumbnails(prev => ({ ...prev, [ad.ad_id]: thumb }));
      }
    })();
    return () => { cancelled = true; };
  }, [paginatedAds, thumbnails]);

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: `${label} copied to clipboard`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setSnackbar({
        open: true,
        message: `Failed to copy ${label} to clipboard`,
        severity: 'error'
      });
    }
  };

  // Generate USPs for an ad with existing transcription
  const handleGenerateUsps = async (adId: string) => {
    try {
      setSnackbar({
        open: true,
        message: `Generating USPs for ${adId}...`,
        severity: 'info'
      });

      const result = await apiService.generateUspsForAd(adId);

      if (result.usps && result.usps.length > 0) {
        setSnackbar({
          open: true,
          message: `✅ Generated ${result.usps.length} USPs for ${adId}`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `⚠️ ${result.message || 'USP generation completed'}`,
          severity: 'info'
        });
      }

      // Refresh the ads data to show the new USPs
      refetch();

    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `❌ Failed to generate USPs: ${getErrorMessage(error)}`,
        severity: 'error'
      });
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth={false} sx={{ py: 3, px: 2, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Loading ads data...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 3, px: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load ads data: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Ads Library
          </Typography>
          <Typography variant="body1" color="text.secondary">
          The book of ads, guarded by the family code
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={`${totalCount} ads${totalCount !== ads.length ? ` (showing ${ads.length})` : ''}`}
              color="primary"
              variant="outlined"
            />
            <Tooltip title="Export current page to CSV">
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={exportToCSV}
                size="small"
                disabled={ads.length === 0}
              >
                Export CSV
              </Button>
            </Tooltip>
          </Box>
          <IconButton onClick={clearAllFilters} title="Clear all filters">
            <FilterListIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          {/* Production ID */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={(filterOptions?.productions ?? []).map(String)}
              value={filters.production_id != null ? String(filters.production_id) : null}
              onChange={(_, v) => updateFilter('production_id', v ? Number(v) : null)}
              renderInput={(params) => <TextField {...params} label="Production ID" placeholder="Type to search..." />}
            />
          </Grid>

          {/* Creative # */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={(filterOptions?.creatives ?? []).map(String)}
              value={filters.creative_number != null ? String(filters.creative_number) : null}
              onChange={(_, v) => updateFilter('creative_number', v ? Number(v) : null)}
              renderInput={(params) => <TextField {...params} label="Creative #" placeholder="Type to search..." />}
            />
          </Grid>

          {/* Created By */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={filterOptions?.created_by ?? []}
              value={filters.created_by}
              onChange={(_, v) => updateFilter('created_by', v)}
              renderInput={(params) => <TextField {...params} label="Created By" placeholder="Type to search..." />}
            />
          </Grid>

          {/* Upload Status */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Upload Status</InputLabel>
              <Select
                value={filters.upload_status ?? ''}
                onChange={(e) => updateFilter('upload_status', e.target.value || null)}
                input={<OutlinedInput label="Upload Status" />}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="not_youtube">Not uploaded to YouTube</MenuItem>
                {META_UPLOAD_ENABLED && <MenuItem value="not_meta">Not uploaded to Meta</MenuItem>}
                <MenuItem value="not_applovin">Not uploaded to AppLovin</MenuItem>
                <MenuItem value="not_uploaded">Not uploaded anywhere</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Meant To */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={filterOptions?.meant_to ?? getMeantToPlatforms()}
              value={filters.meant_to}
              onChange={(_, v) => updateFilter('meant_to', v)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Meant To" placeholder={filters.meant_to.length ? "Add platform..." : "Filter by platform"} />
              )}
            />
          </Grid>

          {/* Media Type */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Media Type</InputLabel>
              <Select
                value={filters.media_type ?? ''}
                onChange={(e) => updateFilter('media_type', e.target.value || null)}
                input={<OutlinedInput label="Media Type" />}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range - From */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Updated From" type="date"
              value={filters.date_from ?? ''}
              onChange={(e) => updateFilter('date_from', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filterOptions?.date_range?.min, max: filters.date_to ?? filterOptions?.date_range?.max }}
            />
          </Grid>

          {/* Date Range - To */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Updated To" type="date"
              value={filters.date_to ?? ''}
              onChange={(e) => updateFilter('date_to', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.date_from ?? filterOptions?.date_range?.min, max: filterOptions?.date_range?.max }}
            />
          </Grid>

          {/* Include Disabled */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl component="fieldset" size="small">
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                <Checkbox
                  checked={includeDisabled}
                  onChange={(e) => { setIncludeDisabled(e.target.checked); setCursor(null); setCursorHistory([]); }}
                  size="small"
                />
                <Typography variant="body2">Include Disabled Ads</Typography>
              </Box>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>


      {/* Batch Actions Bar */}
      {selectedAds.size > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowSelectionDialog(true)}
                sx={{
                  color: 'primary.contrastText',
                  borderColor: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                }}
              >
                {selectedAds.size} Selected Ads
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<YouTubeIcon />}
                onClick={() => handleBatchUpload('youtube')}
                disabled={isBatchUploading}
              >
                Upload to YouTube
              </Button>
              {META_UPLOAD_ENABLED && (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<MetaIcon />}
                  onClick={() => handleBatchUpload('meta')}
                  disabled={isBatchUploading || isRefetchingAfterUpload}
                >
                  Upload to Meta
                </Button>
              )}
              {META_UPLOAD_ENABLED && (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<MetaIcon />}
                  onClick={() => setShowCreateMetaAdDialog(true)}
                  disabled={isBatchUploading || isRefetchingAfterUpload}
                >
                  Create Meta Ad
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AppLovinIcon />}
                onClick={() => setShowApplovinDialog(true)}
                disabled={isBatchUploading || isRefetchingAfterUpload}
                sx={{
                  backgroundColor: '#00B460',
                  '&:hover': { backgroundColor: '#009950' },
                }}
              >
                Upload to AppLovin
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<BatchDownloadIcon />}
                onClick={handleBatchDownload}
                disabled={isBatchUploading}
              >
                Download ZIP
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedAds(new Set())}
                disabled={isBatchUploading}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
          {isBatchUploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {batchUploadType === 'youtube' ? 'Uploading to YouTube...' :
                 batchUploadType === 'meta' ? 'Uploading to Meta...' :
                 batchUploadType === 'applovin' ? 'Uploading to AppLovin...' :
                 'Creating ZIP file...'}
                {batchProgress.current && ` (${batchProgress.current})`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(batchProgress.completed / batchProgress.total) * 100}
              />
              <Typography variant="caption">
                {batchProgress.completed} / {batchProgress.total}
              </Typography>
            </Box>
          )}
          {isRefetchingAfterUpload && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Waiting for upload to complete...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please wait while files are being processed by Meta/YouTube
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer sx={{ maxHeight: '70vh', width: '100%' }}>
          <Table stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 50 }}>
                  <Checkbox
                    indeterminate={selectedAds.size > 0 && !isAllSelected}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ width: 100 }}>Preview</TableCell>
                <TableCell sx={{ width: 110 }}>
                  Updated
                </TableCell>
                <TableCell sx={{ width: 380 }}>
                  Ad ID
                </TableCell>
                <TableCell sx={{ width: 85 }}>
                  Production
                </TableCell>
                <TableCell sx={{ width: 95 }}>
                  Creative #
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                  Name
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  Created By
                </TableCell>
                <TableCell sx={{ width: 110 }}>Upload Status</TableCell>
                <TableCell sx={{ width: 110 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAds.map((ad) => (
                <TableRow key={ad.ad_id} hover>
                  {/* Checkbox */}
                  <TableCell padding="checkbox" sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: ad.has_activity_7d ? 'success.main' : 'grey.300',
                  }}>
                    <Checkbox
                      checked={!isHtmlAd(ad) && selectedAds.has(ad.ad_id)}
                      onChange={(e) => handleSelectAd(ad.ad_id, e.target.checked)}
                      disabled={isHtmlAd(ad)}
                    />
                  </TableCell>
                  {/* Preview */}
                  <TableCell>
                    <Box
                      sx={{
                        width: 72,
                        height: 40,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {(() => {
                        if (isHtmlAd(ad)) {
                          return (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                              HTML
                            </Typography>
                          );
                        }
                        const isImg = isImageAd(ad);
                        if (isImg) {
                          return (
                            <img
                              src={ad.video_url}
                              alt="preview"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              loading="lazy"
                            />
                          );
                        }
                        const preview = thumbnails[ad.ad_id];
                        if (preview) {
                          return (
                            <img
                              src={preview}
                              alt="preview"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              loading="lazy"
                            />
                          );
                        }
                        return <Box sx={{ width: '100%', height: '100%', bgcolor: 'action.hover' }} />;
                      })()}
                    </Box>
                  </TableCell>
                  {/* Updated Date */}
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(ad.updated_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  
                  {/* Ad ID */}
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        lineHeight: 1.3
                      }}
                    >
                      {ad.ad_id}
                    </Typography>
                  </TableCell>
                  
                  {/* Production ID */}
                  <TableCell>{ad.production_id}</TableCell>
                  
                  {/* Creative Number */}
                  <TableCell>{ad.creative_number}</TableCell>
                  
                  {/* Name */}
                  <TableCell>
                    <Typography variant="body2">
                      {ad.name_en}
                    </Typography>
                  </TableCell>
                  
                  {/* Created By */}
                  <TableCell>{ad.created_by}</TableCell>

                  {/* Upload Status */}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Tooltip title={ad.youtube_video_id ? `Click to copy YouTube ID: ${ad.youtube_video_id}` : 'Not uploaded to YouTube'}>
                        <YouTubeIcon
                          sx={{
                            fontSize: 16,
                            color: ad.youtube_video_id ? '#FF0000' : '#666666',
                            opacity: ad.youtube_video_id ? 1 : 0.5,
                            cursor: ad.youtube_video_id ? 'pointer' : 'default'
                          }}
                          onClick={ad.youtube_video_id ? () => copyToClipboard(ad.youtube_video_id!, 'YouTube ID') : undefined}
                        />
                      </Tooltip>
                      {META_UPLOAD_ENABLED && (() => {
                        const uploaded = isUploadedToAnyMeta(ad);
                        const metaAdIds = ad.meta_ad_ids;
                        const uploadedAccounts = metaAccounts.filter(acct => metaAdIds?.[acct.key]);
                        const tooltipText = uploaded
                          ? uploadedAccounts.length > 0
                            ? `Uploaded to: ${uploadedAccounts.map(a => a.label).join(', ')}`
                            : `Click to copy Meta ID: ${ad.meta_ad_id}`
                          : 'Not uploaded to Meta';
                        const firstId = metaAdIds ? Object.values(metaAdIds)[0] : ad.meta_ad_id;
                        return (
                          <Tooltip title={tooltipText}>
                            <MetaIcon
                              sx={{
                                fontSize: 16,
                                color: uploaded ? '#1877F2' : '#666666',
                                opacity: uploaded ? 1 : 0.5,
                                cursor: uploaded ? 'pointer' : 'default'
                              }}
                              onClick={uploaded && firstId ? () => copyToClipboard(firstId, 'Meta ID') : undefined}
                            />
                          </Tooltip>
                        );
                      })()}
                      <Tooltip title={ad.applovin_creative_set_id
                        ? `AppLovin CS: ${ad.applovin_creative_set_id}`
                        : 'Not uploaded to AppLovin'}>
                        <AppLovinIcon
                          sx={{
                            fontSize: 16,
                            color: ad.applovin_creative_set_id ? '#00B460' : '#666666',
                            opacity: ad.applovin_creative_set_id ? 1 : 0.5,
                            cursor: ad.applovin_creative_set_id ? 'pointer' : 'default'
                          }}
                          onClick={ad.applovin_creative_set_id
                            ? () => copyToClipboard(ad.applovin_creative_set_id!, 'AppLovin CS ID')
                            : undefined}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  
                  {/* Actions */}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Generate USPs Button - show if authenticated and ad has transcription */}
                      {isAuthenticated && ad.metadata?.transcription?.status === 'completed' && (
                        <Tooltip title="Generate USPs from existing transcription">
                          <IconButton
                            size="small"
                            onClick={() => handleGenerateUsps(ad.ad_id)}
                            color="primary"
                          >
                            <span style={{ fontSize: '1rem' }}>🎯</span>
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Go to ad page">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Use current domain instead of hardcoded production URL
                            // URL encode the ad ID to handle special characters
                            const currentOrigin = window.location.origin;
                            const encodedAdId = encodeURIComponent(ad.ad_id);
                            window.open(`${currentOrigin}/ads/${encodedAdId}`, '_blank');
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Page {currentPageNumber}{totalPages > 0 ? ` of ${totalPages}` : ''} ({totalCount} total ads)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<PrevIcon />}
              onClick={handlePrevPage}
              disabled={cursorHistory.length === 0}
            >
              Previous
            </Button>
            <Button
              size="small"
              endIcon={<NextIcon />}
              onClick={handleNextPage}
              disabled={!nextCursor}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Meta Account Picker Dialog */}
      <Dialog open={showMetaAccountPicker} onClose={() => setShowMetaAccountPicker(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Meta Ad Account</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Account</InputLabel>
            <Select
              value={selectedMetaAccount}
              label="Account"
              onChange={(e) => setSelectedMetaAccount(e.target.value)}
            >
              {metaAccounts.map(account => (
                <MenuItem key={account.key} value={account.key}>
                  {account.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {pendingMetaUploadAds.length} ad(s) will be uploaded to the selected account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMetaAccountPicker(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMetaAccountPickerConfirm}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Selected Ads Dialog */}
      <Dialog open={showSelectionDialog} onClose={() => setShowSelectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedAds.size} Selected Ads</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {Array.from(selectedAds).map(adId => (
              <Box key={adId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {adId}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSelectionDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Batch Results Dialog */}
      <Dialog open={showBatchDialog} onClose={() => setShowBatchDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Batch {batchUploadType === 'download' ? 'Download' : `${batchUploadType} Upload`} Results
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {batchResults.map((result, index) => (
              <Box key={result.ad_id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: result.success ? 'success.main' : 'error.main',
                    mr: 1
                  }}
                />
                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                  {result.ad_id}
                </Typography>
                {!result.success && (
                  <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                    {result.error}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBatchDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create Meta Ad Dialog */}
      <CreateMetaAdDialog
        open={showCreateMetaAdDialog}
        onClose={() => setShowCreateMetaAdDialog(false)}
        selectedAdIds={Array.from(selectedAds)}
      />

      {/* AppLovin Upload Dialog */}
      <ApplovinUploadDialog
        open={showApplovinDialog}
        onClose={() => setShowApplovinDialog(false)}
        onConfirm={handleApplovinDialogConfirm}
        adCount={selectedAds.size}
        isUploading={isBatchUploading && batchUploadType === 'applovin'}
      />
    </Container>
  );
};

export default AdsTablePage;